import React, { useCallback } from 'react';
import * as Styles from './DomainItem.styles';
import {
  Button, Tag, Toggle, Tooltip, IconSpeed,
} from 'gatorcomponents';
import { ORIGIN, STATUS } from '../../CloudFlareHandler.types';
import PropTypes from 'prop-types';
import { cloudflareAnalytics } from '@/analytics/cloudflare';

export const DomainItem = ({
  domain,
  status,
  origin,
  nameServerTooltipContent,
  activateCdn,
  manageLabel,
  messageOngoingActivation,
  messageOngoingExternalActivation,
  messageOngoingDeactivation,
  messageExternalDomain,
  statusLabel,
  pendingTooltip,
  cdnAlreadyActive,
  manageDomainCdn,
  onClickToggle,
  onVerifyExternalDomain,
  messageWithActivationError,
  messageWithDeactivationError,
}) => {
  const isPendingDeactivation = [STATUS.PENDING_DEACTIVATION].includes(status);
  const isPendingActivation = [
    STATUS.PENDING,
    STATUS.EXTERNAL_PROVIDER,
    STATUS.ACTIVATION_POINTING_ERROR,
  ].includes(status);
  const isDelayed = [STATUS.DELAY].includes(status);
  const isInactive = [STATUS.NONEXISTENT, STATUS.INACTIVE, STATUS.INACTIVE_DELAY].includes(status);
  const hasError = [STATUS.ERROR, STATUS.INACTIVATION_POINTING_ERROR].includes(status);

  const toggleOff = isPendingDeactivation || isPendingActivation || [STATUS.ERROR].includes(status);

  const withTooltip = isPendingActivation || status === STATUS.ACTIVE;
  const canManageCDN = (
    !isPendingActivation
    && !isPendingDeactivation
    && !isInactive
    && (!hasError)
    && !isDelayed
  );

  const hasMessageAndTag = isPendingDeactivation
  || isPendingActivation
  || isDelayed
  || hasError
  || (status === STATUS.NONEXISTENT && origin === ORIGIN.EXTERNAL);

  const handleVerifyDomain = () => {
    onVerifyExternalDomain && onVerifyExternalDomain();
  };

  const mountMessage = useCallback(() => {
    if (isDelayed || hasError) {
      if (status === STATUS.PENDING_DEACTIVATION) {
        return messageWithDeactivationError;
      }

      return messageWithActivationError;
    }

    if (isPendingDeactivation) {
      return messageOngoingDeactivation;
    }

    if (isPendingActivation && origin === ORIGIN.EXTERNAL) {
      return messageOngoingExternalActivation;
    }

    if (isPendingActivation) {
      return messageOngoingActivation;
    }

    if (status === STATUS.NONEXISTENT && origin === ORIGIN.EXTERNAL) {
      return messageExternalDomain;
    }

    return '';
  }, [
    isPendingActivation,
    isPendingDeactivation,
    messageExternalDomain,
    messageOngoingActivation,
    messageOngoingDeactivation,
    origin,
    status,
    hasError,
    isDelayed,
    messageWithActivationError,
    messageWithDeactivationError,
    messageOngoingExternalActivation,
  ]);

  const getTagVariant = useCallback(() => {
    if ([STATUS.PENDING, STATUS.ACTIVATION_POINTING_ERROR, STATUS.INACTIVATION_POINTING_ERROR].includes(status) && origin === ORIGIN.EXTERNAL) {
      return 'attention';
    }

    if (hasError || isDelayed) {
      return 'problem';
    }

    if (isPendingActivation || isPendingDeactivation) {
      return 'info';
    }

    if (status === STATUS.NONEXISTENT && origin === ORIGIN.EXTERNAL) {
      return 'pending';
    }
  }, [
    isPendingActivation,
    isPendingDeactivation,
    origin,
    status,
    hasError,
    isDelayed,
  ]);

  const getToggleCheckedStatus = useCallback(() => {
    if (isPendingDeactivation) {
      return true;
    }

    return status === STATUS.ACTIVE || isPendingActivation;
  }, [
    isPendingActivation,
    isPendingDeactivation,
    status,
  ]);

  const activeTooltipContent = () => (
    <Styles.ActiveTooltipContentWrapper>
      <IconSpeed
        secondaryColor="activeLight"
        size="lg"
      />
      {cdnAlreadyActive}
    </Styles.ActiveTooltipContentWrapper>
  );

  const handleManageDomainCDN = () => {
    cloudflareAnalytics.manageCta();
    manageDomainCdn && manageDomainCdn();
  };

  const isInternalOrOngoinging = (
    origin === ORIGIN.INTERNAL
    || (
      origin === ORIGIN.EXTERNAL
      && ![STATUS.NONEXISTENT, STATUS.INACTIVE, STATUS.DELAY, STATUS.INACTIVE_DELAY].includes(status)
    )
  );

  const callGaTag = () => {
    if (origin === ORIGIN.INTERNAL && status === STATUS.ACTIVE) {
      cloudflareAnalytics.deactivateHgDomainToggle();
    }
    if (origin === ORIGIN.INTERNAL && status === STATUS.INACTIVE) {
      cloudflareAnalytics.activateHgDomainToggle();
    }
    if (origin === ORIGIN.EXTERNAL && status === STATUS.ACTIVE) {
      cloudflareAnalytics.deactivateExternalDomainToggle();
    }
    if (origin === ORIGIN.EXTERNAL && status === STATUS.INACTIVE) {
      cloudflareAnalytics.activateExternalDomainToggle();
    }
  };

  const handleClickToggle = (e) => {
    e.preventDefault();

    if (toggleOff) {
      return;
    }

    callGaTag();

    if (isInternalOrOngoinging) {
      onClickToggle && onClickToggle();
      return;
    }

    if (!isInternalOrOngoinging) {
      handleVerifyDomain && handleVerifyDomain();
    }
  };

  return (
    <Styles.CardWrapper data-testid="domainItem">
      {nameServerTooltipContent ? (
        <Tooltip content={nameServerTooltipContent}>
          <Styles.DomainName data-testid="domain">
            {domain}
          </Styles.DomainName>
        </Tooltip>
      ) : (
        <Styles.DomainName data-testid="domain">
          {domain}
        </Styles.DomainName>
      )}

      {hasMessageAndTag && (
        <Styles.TagWrapper data-testid={`${status.toLowerCase().replace(/_/g, '')}${origin.toLowerCase()}`}>
          <Tag
            testId={`Tag-${domain}`}
            label={
              statusLabel[
                `${status.toLowerCase().replace(/_/g, '')}${origin.toLowerCase()}`
              ]
            }
            variant={getTagVariant()}
          />
        </Styles.TagWrapper>
      )}

      <Styles.LeftColumn>
        <>
          <Styles.ToggleWrapper>
            <Tooltip
              content={status === STATUS.ACTIVE ? activeTooltipContent() : pendingTooltip}
              preferredPosition="topLeft"
              variant={status === STATUS.ACTIVE ? 'light' : 'default'}
              disabled={!withTooltip}
            >
              <Styles.ToggleLabel onClick={e => handleClickToggle(e)}>
                <Toggle
                  checked={getToggleCheckedStatus()}
                  testId={`toggle-${domain}`}
                  disabled={toggleOff}
                  controlledByFather
                />
              </Styles.ToggleLabel>
            </Tooltip>

            <Styles.ToggleText>
              {activateCdn}
            </Styles.ToggleText>
          </Styles.ToggleWrapper>


          <Button
            testId="manageCta"
            label={manageLabel}
            size="medium2"
            disabled={!canManageCDN}
            onClick={handleManageDomainCDN}
          />
        </>
      </Styles.LeftColumn>

      {hasMessageAndTag && (
        <Styles.ItemDescription data-testid="message">
          {mountMessage()}
        </Styles.ItemDescription>
      )}
    </Styles.CardWrapper>
  );
};

DomainItem.propTypes = {
  domain: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  origin: PropTypes.string.isRequired,
  nameServerTooltipContent: PropTypes.object,
  activateCdn: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  manageLabel: PropTypes.string,
  messageOngoingActivation: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  messageOngoingDeactivation: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  messageExternalDomain: PropTypes.string.isRequired,
  statusLabel: PropTypes.object.isRequired,
  pendingTooltip: PropTypes.string,
  cdnAlreadyActive: PropTypes.object,
  manageDomainCdn: PropTypes.func.isRequired,
  onClickToggle: PropTypes.func,
  onVerifyExternalDomain: PropTypes.func,
};


export default DomainItem;
